/**
 * Live Bot Monitor: FanCentro en F2F apart.
 *
 * `/api/admin/posting/live-batch` geeft de creators die op dit moment een post
 * uitvoeren, elk met zijn platform, plus `slots` met de werkelijke capaciteit
 * per platform.
 *
 * Waarom de twee blokken niet even groot zijn
 * -------------------------------------------
 * FanCentro heeft een `CreatorSessionPool`: elk model krijgt zijn eigen
 * geïsoleerde browsercontext, dus daar draaien er echt vijf naast elkaar. F2F
 * publiceert via één gedeelde ingelogde pagina achter `F2FAuth.page_lock` — één
 * browser, één post tegelijk. Vijf F2F-schermen tekenen zou een capaciteit
 * suggereren die er niet is; het aantal komt daarom uit `slots` en niet uit een
 * vast getal hier. Zodra er een F2F-sessiepool is, vult dit blok zich vanzelf.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import ModelOverzicht from './ModelOverzicht'
import { Panel, Plat, Tag } from './ui'
import { api, getToken } from '../lib/api'
import { useApi, usePoll } from '../lib/useApi'
import { num } from '../lib/format'

interface VaultStats {
  tracked_bot_vault_rows: number
  remaining_due: number
  by_status?: Record<string, number>
  deleted_success_since_restart?: number
  unlink_accepted_since_restart?: number
}

const POLL_MS = 2000

/**
 * Een live beeld dat niet knippert.
 *
 * 15-09 (Jordy). Elke ververs gaf het `<img>` een nieuwe `src` met
 * `Date.now()`, ook als er geen nieuw frame was; de browser haalde het beeld
 * dan opnieuw op en toonde tussendoor niets. Nu verandert de `src` alleen als
 * het frame echt nieuw is (`captured_at`), en het nieuwe beeld wordt eerst op
 * de achtergrond geladen. Pas als het er is, wordt het oude vervangen.
 */
function StabielBeeld({ src, alt, style, onError }: {
  src: string; alt: string; style?: CSSProperties; onError?: () => void
}) {
  const [getoond, setGetoond] = useState(src)
  // De foutafhandeling komt elke render als nieuwe functie binnen; via een ref
  // hoeft het laden daarvoor niet opnieuw te beginnen.
  const fout = useRef(onError)
  fout.current = onError
  useEffect(() => {
    if (src === getoond) return
    let weg = false
    const laden = new Image()
    laden.onload = () => { if (!weg) setGetoond(src) }
    laden.onerror = () => { if (!weg) fout.current?.() }
    laden.src = src
    return () => { weg = true }
  }, [src, getoond])
  return <img src={getoond} alt={alt} style={style} onError={() => fout.current?.()} />
}
const STALE_MS = 15_000

/** Capaciteit als de backend nog niets zegt: FC vijf, F2F één. */
const SLOTS_FALLBACK = { fc: 5, f2f: 1 }

interface BatchItem {
  creator_id: string
  name: string
  image_url: string
  captured_at: string | null
  step?: string
  /** 'fc' of 'f2f'; ontbreekt bij een oudere worker en is dan FanCentro. */
  platform?: string
  /** Waarmee een scherm aan zijn stappenlijst gekoppeld wordt. */
  model_persona_id?: number | null
  /** 'verifying' = sessie wordt geopend, met opzet nog geen beeld.
   *  'posting'   = identiteit bevestigd, er hoort een beeld te zijn.
   *  'wacht'     = dit model is aan, maar heeft nu geen open pagina. */
  state?: string
  /** Publiceert deze creator nu echt? Alleen waar als de worker een lane heeft
   *  én steps.json een lopende, recente post voor dit model toont. */
  posting?: boolean
  /** Wanneer de volgende post van dit model wordt overgedragen. */
  volgende?: string | null
  /** Waar dit model in zijn cyclus staat; voor de kopregel van de tegel. */
  item?: number | null
  of_total?: number | null
  /** Hoe lang het openen al duurt, in seconden. */
  seconds?: number | null
}
/** Eén mislukte post, zoals /recent-failures hem geeft. */
interface MisluktePost {
  id: number
  model_persona_id: number
  platform: string
  caption: string | null
  description?: string | null
  created_by?: string | null
  status: string
  ui_status: string
  error_message: string | null
  retry_count: number
  upload_pogingen?: number
  scheduled_at: string | null
  updated_at: string | null
}

/** Een post die FanCentro steeds weigert (27-09, `/steeds-mislukt`). */
interface SteedsMisluktePost {
  post_id: number
  model_persona_id: number
  model: string | null
  platform: string | null
  caption: string | null
  description: string | null
  status: string
  created_by: string | null
  last_reason: string
  last_at: string | null
  /** Alle mislukte pogingen die de worker telde. */
  attempts: number
  in_a_row: number
  /** Alleen gevuld zolang de post nu uit de rij staat. */
  parked_until: string | null
  times_parked: number
  needs_attention: boolean
}

/** Wat de monitor van een model moet weten om er een tegel voor te tekenen. */
interface VoortgangModelLite {
  model_persona_id: number
  name: string
  creator_id: string
  active: boolean
  item: number | null
  of_total: number | null
  next_handover: string | null
  scheduled_through: string | null
}

/**
 * Korte kop per stap, voor de kolommen van de Live Steps-tabel.
 *
 * De volle labels ("Open creator session", "Verify creator identity") zijn
 * geschreven voor een lijst onder elkaar. Naast elkaar in zes kolommen maken ze
 * van elke kop drie regels en dan past er geen model meer op een scherm. De
 * volle tekst blijft in de tooltip staan.
 *
 * De volgorde van de sleutels is de volgorde van de code, niet van dit object:
 * de tabel loopt de stappen af zoals `/live-steps` ze levert.
 */
const KOP: Record<string, string> = {
  session: 'Session',
  identity: 'Identity',
  media: 'Media',
  caption: 'Caption',
  publish: 'Publish',
  verify: 'Verify',
  lifecycle: 'Expiry · vault',
}

/** Eén stap van één post, zoals /live-steps hem geeft. */
interface Stap {
  key: string
  label: string
  state: 'pending' | 'running' | 'done' | 'failed' | 'skipped' | 'waiting' | 'moved'
  seconds: number | null
  reason: string
}
interface PostStappen {
  post_id: number
  model_persona_id: number
  model: string
  platform: string
  cycle: number | null
  item: number | null
  of_total: number | null
  started_at: number | null
  finished_at: number | null
  outcome: string
  steps: Stap[]
}

interface Batch {
  max_parallel: number
  items?: BatchItem[]
  slots?: { fc?: number; f2f?: number }
}

/** Hoe lang dit scherm al bezig is, als "1m 20s". */
/** Hoe oud is dit beeld? Leeg als we het niet weten. */
function leeftijdS(vanaf: string | null | undefined): number | null {
  if (!vanaf) return null
  const ms = Date.now() - new Date(vanaf).getTime()
  if (!Number.isFinite(ms) || ms < 0) return null
  return Math.floor(ms / 1000)
}

function sinds(vanaf: string | null | undefined): string {
  const s = leeftijdS(vanaf)
  if (s === null) return ''
  // "ago" erbij (Jordy, 08-09): een kaal "42s" naast een stilstaand beeld las
  // als een duur, niet als de ouderdom van het plaatje.
  return s < 60 ? `${s}s ago` : `${Math.floor(s / 60)}m ${s % 60}s ago`
}

/** De naam bij een model_persona_id; valt terug op het id als we hem niet weten. */
function modelNaam(id: number, modellen?: VoortgangModelLite[]): string {
  const m = (modellen ?? []).find((x) => x.model_persona_id === id)
  return m?.name || `model ${id}`
}

/** "11-09 14:20" uit een naïeve UTC-tijdstempel uit de database. */
/** Als `klokje`, maar met seconden: het exacte doorzet-moment. */
function seconden(iso: string | null | undefined): string {
  if (!iso) return '—'
  const ruw = String(iso)
  const d = new Date(ruw.includes('Z') || ruw.includes('+') ? ruw : ruw + 'Z')
  if (Number.isNaN(d.getTime())) return '—'
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}-${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}

function klokje(iso: string | null | undefined): string {
  if (!iso) return '—'
  const ruw = String(iso)
  const d = new Date(ruw.includes('Z') || ruw.includes('+') ? ruw : ruw + 'Z')
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('nl-NL', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  }).replace(',', '')
}

/** Ouder dan dit heet niet meer live; het beeld blijft staan, maar grijs. */
const VERS_S = 12

/** Na drie minuten zonder nieuwe stand is een stappenlijst geen lopende post meer.
 *
 *  08-09 (Jordy): een tegel stond 46 minuten op "Open creator session 0.0s" en
 *  las als een post die vastzat. Dat was hij niet — de post was in 31 seconden
 *  klaar, maar het stappenboek bewaart een post 24 uur en de tegel toonde de
 *  laatst weggeschreven stand. Zonder leeftijd erbij is oud niet van lopend te
 *  onderscheiden. */
const STAND_VERS_S = 180

/** Hoe lang een beeld nog als "nu" mag gelden. Ruimer dan VERS_S (dat kleurt
 *  alleen de leeftijdstekst), want een post duurt tientallen seconden en een
 *  tegel hoort niet bij elke trage stap te vergrijzen. Erboven is het geen
 *  live beeld meer maar een foto van eerder. */
const BEELD_VERS_S = 120

/** Is dit beeld te oud om voor nu door te gaan?
 *
 *  11-09-2026. Jordy zag "Oops! Page Not Found" op vijf tegels. Dat waren
 *  plaatjes van de avond ervóór, tot vijftien uur oud: frames blijven in
 *  /dev/shm staan tot ze overschreven worden, en een model dat aan staat maar
 *  nu niet post krijgt wél een tegel. Zonder dit onderscheid leest elk oud
 *  plaatje als de huidige stand van die browser. */
function beeldVerouderd(it: { captured_at: string | null; state?: string }): boolean {
  // Tijdens het openen van de sessie is er met opzet nog geen beeld; dan is
  // "oud" niet het goede woord en toont de tegel toch al geen plaatje.
  if (it.state === 'verifying') return false
  const oud = leeftijdS(it.captured_at)
  return oud !== null && oud > BEELD_VERS_S
}

/** Hoe oud is deze stappenlijst, in seconden? */
function standLeeftijd(pv: { started_at: number | null; finished_at: number | null;
                             steps: Stap[] }): number | null {
  const laatste = pv.finished_at ?? pv.started_at
  if (!laatste) return null
  // De stappen dragen hun eigen duur; de jongste beweging is start + som.
  const gelopen = pv.steps.reduce((t, s) => t + (s.seconds ?? 0), 0)
  const moment = (pv.finished_at ?? (pv.started_at ?? 0) + gelopen) * 1000
  const s = Math.floor((Date.now() - moment) / 1000)
  return s >= 0 ? s : null
}

export interface QueueRowLite {
  id: number
  platform: string
  status: string
  caption: string | null
  model?: string | null
  scheduled_at?: string | null
  published_at?: string | null
}

export function LiveBrowser({
  vault,
  liveLoading,
  queue,
}: {
  vault: VaultStats | null
  liveLoading: boolean
  /** De wachtrij voedt POSTS IN PROGRESS en RECENTLY PUBLISHED. */
  queue: QueueRowLite[]
}) {
  const [items, setItems] = useState<BatchItem[]>([])
  const [slots, setSlots] = useState(SLOTS_FALLBACK)
  /** Creators waarvan het beeld nu niet op te halen is. Wordt bij elke ronde
   *  geleegd, zodat een scherm vanzelf terugkomt zodra er weer een frame is. */
  const [kapot, setKapot] = useState<Set<string>>(new Set())
  /** De stappenlijst per post. Afgeronde posts blijven 24 uur staan. */
  const stappen = useApi<{ posts: PostStappen[] }>('/api/admin/posting/live-steps')
  /** De actieve FC-modellen. Hieruit komen de tegels — één per model, niet één
   *  per open browserpagina.
   *
   *  08-09 (Jordy: "ik zie maar 1 live staan"): een tegel bestond alleen zolang
   *  een model een pool-pagina vasthield. Een model dat aan "Verify post
   *  appeared" bezig is heeft die pagina al teruggegeven, dus het verdween van
   *  het scherm terwijl het nog aan het werk was. In de Live Steps stonden er
   *  vier te draaien en op de tegels één. */
  const modellen = useApi<{ models: VoortgangModelLite[] }>(
    '/api/admin/posting/voortgang')
  usePoll(5000, modellen.refetch)
  /** Posts die zijn mislukt, met de reden erbij.
   *
   *  09-09 (Jordy): "ik wil een lijst met posts als ze fout zijn gegaan en
   *  precies de reden waarom". De reden stond alleen in het journaal, en dat
   *  is geen plek om 's ochtends te gaan zoeken. Deze route bestond al
   *  (`recent-failures`) maar werd nergens getoond. */
  const fouten = useApi<{ items: MisluktePost[]; count: number }>(
    '/api/admin/posting/recent-failures?hours=24')
  usePoll(15000, fouten.refetch)
  /** 27-09 (Jordy): posts die FanCentro steeds weigert en die de bot daarom
   *  telkens een tijd uit de rij haalt. De worker bewaart die stand in de
   *  database; zelfde verversing als het blok met mislukte posts. */
  const steeds = useApi<{ items: SteedsMisluktePost[]; count: number; needs_attention: number }>(
    '/api/admin/posting/steeds-mislukt')
  usePoll(15000, steeds.refetch)
  usePoll(2000, stappen.refetch)
  /** 15-09 (Jordy): de tekst van een mislukte post hier verbeteren en hem
   *  terugzetten in het rooster, zonder via de wachtrij te zoeken. */
  const [bewerk, setBewerk] = useState<{ id: number; caption: string; description: string } | null>(null)
  const [bewerkMelding, setBewerkMelding] = useState<{ id: number; tekst: string; fout: boolean } | null>(null)
  const [bewerkBezig, setBewerkBezig] = useState(false)
  const bewaarBewerking = async () => {
    if (!bewerk) return
    setBewerkBezig(true)
    try {
      const uit = await api.post<{ scheduled_at: string | null; queue_item_updated: boolean }>(
        `/api/admin/posting/scheduled/${bewerk.id}/fix-and-requeue`,
        { caption: bewerk.caption, description: bewerk.description })
      setBewerkMelding({
        id: bewerk.id, fout: false,
        tekst: `Back in the schedule, goes live ${klokje(uit?.scheduled_at)}`
          + (uit?.queue_item_updated ? '; the queue text is fixed too' : ''),
      })
      setBewerk(null)
      fouten.refetch()
      steeds.refetch()
    } catch (e) {
      setBewerkMelding({ id: bewerk.id, fout: true, tekst: e instanceof Error ? e.message : String(e) })
    } finally {
      setBewerkBezig(false)
    }
  }
  // 27-09: de Edit-knop en het bewerkvak staan nu in twee blokken (mislukte
  // posts en "Posts that keep failing"). Eén keer gedefinieerd, zodat ze in
  // beide precies hetzelfde doen: alleen een mislukte post van de bot, want
  // `fix-and-requeue` weigert elke andere status.
  const bewerkKnop = (p: { id: number; status: string; created_by?: string | null
                           caption: string | null; description?: string | null }) =>
    String(p.status) === 'failed' && p.created_by === 'posting_bot' ? (
      <button className="btn ghost sm" type="button"
              onClick={() => {
                setBewerkMelding(null)
                setBewerk({ id: p.id, caption: p.caption ?? '', description: p.description ?? '' })
              }}>Edit</button>
    ) : null
  const bewerkVak = (id: number) => (
    <>
      {bewerk && bewerk.id === id ? (
        <div style={{ flexBasis: '100%', marginTop: 6 }}>
          <label className="mut2" style={{ fontSize: 11 }}>Title</label>
          <input className="inp" style={{ width: "100%" }} value={bewerk.caption}
                 onChange={(e) => setBewerk({ ...bewerk, caption: e.target.value })} />
          <label className="mut2" style={{ fontSize: 11 }}>Description</label>
          <input className="inp" style={{ width: "100%" }} value={bewerk.description}
                 onChange={(e) => setBewerk({ ...bewerk, description: e.target.value })} />
          <div className="row" style={{ gap: 8, marginTop: 6 }}>
            <button className="btn primary sm" type="button" disabled={bewerkBezig}
                    onClick={() => bewaarBewerking()}>
              {bewerkBezig ? 'Saving…' : 'Save and schedule again'}
            </button>
            <button className="btn ghost sm" type="button" disabled={bewerkBezig}
                    onClick={() => setBewerk(null)}>Cancel</button>
            <span className="mut2" style={{ fontSize: 11 }}>
              FanCentro needs a title of at least 10 characters and no blocked terms.
            </span>
          </div>
        </div>
      ) : null}
      {bewerkMelding && bewerkMelding.id === id ? (
        <div style={{ flexBasis: '100%', fontSize: 11,
                      color: bewerkMelding.fout ? 'var(--bad)' : 'var(--ok)' }}>
          {bewerkMelding.tekst}
        </div>
      ) : null}
    </>
  )

  const platformVan = (x: BatchItem) => (x.platform || 'fc').toLowerCase()
  /**
   * Een tegel per FC-model dat NU een live stap uitvoert.
   *
   * 15-09 (Jordy): "Toon alleen tegels van modellen die nu écht een live stap
   * aan het uitvoeren zijn." Hiervoor kreeg elk aangezet model een vaste tegel,
   * ook zonder open pagina. Dat gaf zwarte tegels voor modellen die op hun
   * 24-uursvenster wachtten, en "Oops! Page Not Found"-beelden van vijf dagen
   * oud. Nu komen de tegels alleen uit het live-manifest, en alleen als de
   * route `posting` zegt. Wat de configuratie nog toevoegt is de kopregel
   * (item, volgende overdracht).
   */
  const fcTegels: BatchItem[] = useMemo(() => {
    const perCreator = new Map(
      (modellen.data?.models ?? []).map((m) => [String(m.creator_id), m]))
    return items
      .filter((x) => platformVan(x) !== 'f2f')
      .map((it) => {
        const m = perCreator.get(String(it.creator_id))
        return m ? { ...it, name: m.name, model_persona_id: m.model_persona_id,
                     volgende: m.next_handover, item: m.item, of_total: m.of_total }
                 : it
      })
  }, [modellen.data, items])

  const f2fItems = items.filter((x) => platformVan(x) === 'f2f')

  const inProgress = queue
    .filter((r) => ['publishing', 'scheduled', 'failed'].includes(String(r.status)))
    .slice(0, 40)
    .map((r) => ({ id: r.id, platform: r.platform, model: r.model,
                   caption: r.caption, status: r.status,
                   // Datum erbij (Jordy, 08-09). Alleen een tijd las alsof
                   // alles vandaag gebeurt, terwijl de bot tot een maand
                   // vooruit plant: "20:50" kon 9 september zijn of 30
                   // september, en dat scheelt nogal.
                   at: klokje(r.scheduled_at) }))

  /** 15-09 (Jordy, POSTING-010): de laatst doorgezette posts met drie
   *  momenten: doorgezet (exact, met seconden), live, en weer weg. Eigen route
   *  en eigen verversing; de wachtrij toonde alleen posts die binnen drie dagen
   *  live gaan en ververste niet vanzelf. */
  // 15-09 (Jordy): geen beperkte lijst, maar de hele geschiedenis om terug te
  // bladeren, per 50, met dezelfde filters als de wachtrij (model, platform).
  const [recentPagina, setRecentPagina] = useState(0)
  const [recentModel, setRecentModel] = useState('all')
  const [recentPlatform, setRecentPlatform] = useState('all')
  const RECENT_PER_PAGINA = 50
  const recentPad = `/api/admin/posting/recently-pushed?limit=${RECENT_PER_PAGINA}`
    + `&offset=${recentPagina * RECENT_PER_PAGINA}`
    + (recentModel !== 'all' ? `&model_persona_id=${recentModel}` : '')
    + (recentPlatform !== 'all' ? `&platform=${recentPlatform}` : '')
  const recent = useApi<{
    total: number
    models: { model_persona_id: number; model: string | null; count: number }[]
    data: {
      id: number; platform: string; model: string | null; caption: string; status: string
      pushed_at: string | null; goes_live_at: string | null; expires_at: string | null
    }[]
  }>(recentPad, [recentPad])
  usePoll(15000, recent.refetch)
  const recentTotaal = recent.data?.total ?? 0
  const [recentGeladen, setRecentGeladen] = useState<Date | null>(null)
  useEffect(() => { if (recent.data) setRecentGeladen(new Date()) }, [recent.data])
  const published = (recent.data?.data ?? []).map((r) => ({
    id: r.id, platform: r.platform, model: r.model, caption: r.caption, status: r.status,
    pushed: seconden(r.pushed_at), live: klokje(r.goes_live_at),
    expires: klokje(r.expires_at),
    isLive: !!r.goes_live_at && new Date(r.goes_live_at).getTime() <= Date.now(),
  }))

  const [note, setNote] = useState<string | null>('Waiting for the first round…')
  const seen = useRef<Map<string, { item: BatchItem; t: number }>>(new Map())
  /**
   * Welke creator op welk scherm staat (1..N), en dat blijft zo.
   *
   * 08-09 (Jordy): de tegels sprongen tijdens een ronde van plek. De rij werd
   * elke twee seconden opnieuw opgebouwd uit "wie is nu actief", dus zodra een
   * model tussen twee posts even niet actief was, schoven alle modellen erachter
   * een plek op. Met vijf modellen tegelijk was dat een dansende muur.
   *
   * Een creator houdt nu zijn scherm zolang hij in beeld is. Een slot komt pas
   * vrij als hij helemaal uit de lijst valt (STALE_MS), en wordt dan opnieuw
   * uitgedeeld aan het eerste model dat nog geen plek heeft — het laagste vrije
   * nummer, zodat er geen gaten in de rij ontstaan.
   */
  const slotVan = useRef<Map<string, number>>(new Map())

  const wijsSlotsToe = useCallback((rijen: BatchItem[]): BatchItem[] => {
    const kaart = slotVan.current
    const nu = new Set(rijen.map((r) => String(r.creator_id)))
    for (const id of [...kaart.keys()]) {
      if (!nu.has(id)) kaart.delete(id)
    }
    const bezet = new Set(kaart.values())
    for (const r of rijen) {
      const id = String(r.creator_id)
      if (kaart.has(id)) continue
      let n = 1
      while (bezet.has(n)) n += 1
      kaart.set(id, n)
      bezet.add(n)
    }
    return [...rijen].sort(
      (a, b) => (kaart.get(String(a.creator_id)) ?? 99)
              - (kaart.get(String(b.creator_id)) ?? 99))
  }, [])

  const tick = useCallback(async () => {
    try {
      const b = await api.get<Batch>('/api/admin/posting/live-batch')
      const now = Date.now()
      // 15-09 (Jordy): alleen modellen die nu écht een stap uitvoeren. Een
      // open sessie voor opruiming of controle, of een model dat aan staat maar
      // wacht, krijgt geen tegel meer.
      const active = (b.items ?? []).filter((i) => i.posting)
      for (const it of active) seen.current.set(String(it.creator_id), { item: it, t: now })
      const activeIds = new Set(active.map((i) => String(i.creator_id)))
      const rows: BatchItem[] = [...active]
      for (const [id, v] of seen.current) {
        if (activeIds.has(id)) continue
        if (now - v.t > STALE_MS) { seen.current.delete(id); continue }
        rows.push(v.item)
      }
      setItems(wijsSlotsToe(rows))
      // `kapot` niet meer leegmaken bij elke poll: dat zette een tegel die net
      // op een gebroken beeld was gevallen elke twee seconden opnieuw aan, wat
      // precies het geflikker gaf. De route levert nu altijd het laatste frame,
      // dus een echte fout is zeldzaam en mag blijven staan tot het model uit
      // beeld gaat.
      setSlots({
        fc: Number(b.slots?.fc ?? SLOTS_FALLBACK.fc) || SLOTS_FALLBACK.fc,
        f2f: Number(b.slots?.f2f ?? SLOTS_FALLBACK.f2f) || SLOTS_FALLBACK.f2f,
      })
      setNote(rows.some((r) => r.posting) ? null : 'No model posting right now')
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'live-batch unreachable')
    }
  }, [wijsSlotsToe])

  useEffect(() => {
    tick()
    const id = setInterval(tick, POLL_MS)
    return () => clearInterval(id)
  }, [tick])

  const token = getToken()

  /** De post die bij een scherm hoort: op model_persona_id, en anders op naam.
   *  De nieuwste eerst, zodat een tegel de lopende post toont. */
  const postVan = (it: BatchItem): PostStappen | undefined =>
    (stappen.data?.posts ?? []).find(
      (pv) => String(pv.model_persona_id) === String(it.model_persona_id ?? '')
           || pv.model.toLowerCase() === (it.name || '').toLowerCase())

  /** De vijf stappen als afvinklijst: grijs, geel met seconden, groen, rood
   *  met reden, oranje bij overgeslagen. */
  /**
   * De vijf stappen van één post.
   *
   * `klein` is de variant die over een tegel heen ligt; die blijft onder
   * elkaar staan, want daar is de breedte op.
   *
   * In het Live Steps-paneel staan ze van LINKS NAAR RECHTS (Jordy, 08-09):
   * met vijf modellen tegelijk werden het anders vijf lijsten van vijf regels
   * onder elkaar en moest je scrollen om te zien waar iemand stond. Naast
   * elkaar past een model op één regel en zie je de hele ronde in één blik.
   * Bij een smal scherm loopt de rij door op de volgende regel in plaats van
   * horizontaal te scrollen.
   */
  const Stappenlijst = ({ post, klein }: { post: PostStappen; klein?: boolean }) => {
    const stap = (s2: PostStappen['steps'][number]) => {
      const kleur = s2.state === 'done' ? 'var(--ok)'
        : s2.state === 'running' ? 'var(--warn)'
        : s2.state === 'failed' ? 'var(--bad)'
        : s2.state === 'skipped' ? 'var(--acc)'
        : 'var(--mut)'
      return (
        <div key={s2.key}
             style={{ display: 'flex', gap: 6, alignItems: 'baseline',
                      fontSize: klein ? 10 : 12,
                      // Naast elkaar mag een stap niet meekrimpen tot de tekst
                      // wegvalt; onder elkaar mag hij juist de volle breedte
                      // gebruiken.
                      ...(klein ? {} : { flex: '0 0 auto' }) }}
             title={s2.reason || undefined}>
          <span style={{ color: kleur }}>
            {s2.state === 'done' ? '✓' : s2.state === 'failed' ? '✕'
              : s2.state === 'skipped' ? '⊘' : s2.state === 'running' ? '●' : '○'}
          </span>
          <span style={{ color: s2.state === 'pending' ? 'var(--mut)' : undefined,
                         overflow: 'hidden', textOverflow: 'ellipsis',
                         whiteSpace: 'nowrap' }}>{s2.label}</span>
          {s2.seconds != null ? (
            <span className="mut2">{s2.seconds.toFixed(1)}s</span>
          ) : null}
          {s2.reason ? <span className="mut2">· {s2.reason}</span> : null}
        </div>
      )
    }
    if (klein) {
      return <div style={{ display: 'grid', gap: 2 }}>{post.steps.map(stap)}</div>
    }
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 18px',
                    alignItems: 'baseline' }}>
        {post.steps.map(stap)}
      </div>
    )
  }

  /** Eén blok met de schermen van één platform. */
  const Blok = ({ label, rijen, aantal, uitleg }: {
    label: string
    rijen: BatchItem[]
    aantal: number
    uitleg?: string
  }) => (
    <div style={{ marginBottom: 18 }}>
      <div className="between" style={{ marginBottom: 8 }}>
        <b style={{ fontSize: 12 }}>
          {label} · up to {aantal} at once
        </b>
        {/* 15-09 (Jordy): na Stop All stond hier "4/4 active" terwijl er al
            negen uur niets gepost was. Een open sessie voor opruiming of
            controle is geen actieve publicatie; alleen `posting` telt. */}
        {(() => {
          const bezig = rijen.filter((r) => r.posting).length
          return (
            <Tag kind={bezig ? 'ok' : 'mut'}>
              {bezig ? <span className="dotst on-live" /> : null}
              {bezig} / {aantal} posting
            </Tag>
          )
        })()}
      </div>
      {uitleg ? (
        <div className="mut2" style={{ fontSize: 12, marginBottom: 8 }}>{uitleg}</div>
      ) : null}
      {/* Vast vijf kolommen, ook als er minder tegels zijn.
          
          Met `auto-fill, minmax(220px, 1fr)` maakte de browser zoveel kolommen
          als er pasten — op een breed scherm acht — en bleven vijf tegels dus
          smal met een half scherm leeg ernaast. Nu vult een volle rij de hele
          breedte (op 1920px ruim 370px per tegel in plaats van ~200px) en houdt
          een blok met één tegel diezelfde tegelgrootte, zodat FC en F2F er
          hetzelfde uitzien.
          
          Onder 1100px worden het er drie, en op een telefoon twee: vijf naast
          elkaar is daar niet meer te volgen. */}
      {!rijen.length ? (
        <div className="mut2" style={{ fontSize: 12 }}>No model posting right now</div>
      ) : null}
      <div className="grid schermraster">
        {rijen.map((_, i) => {
          const it = rijen[i]
          return (
            <div
              key={it ? String(it.creator_id) : `leeg-${i}`}
              style={{
                position: 'relative', background: '#000', borderRadius: 6,
                overflow: 'hidden', aspectRatio: '16 / 10',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {it && !kapot.has(String(it.creator_id)) ? (
                <>
                  <StabielBeeld
                    src={`${it.image_url}&token=${encodeURIComponent(token)}&v=${encodeURIComponent(it.captured_at ?? '')}`}
                    alt={it.name}
                    style={{
                      width: '100%', height: '100%', objectFit: 'contain',
                      // 11-09 (Jordy): "page not found". Dat plaatje was van de
                      // avond ervóór, vijftien uur oud. Een creator met een
                      // sessie in de pool die NIET aan het posten is houdt zijn
                      // tegel, en die tegel toonde zijn laatste beeld op volle
                      // sterkte. Dat leest als nu. De leeftijd stond er wel
                      // bij, klein en grijs rechtsboven, en dat is te weinig.
                      // Een oud beeld hoort er ook oud uit te zien.
                      filter: beeldVerouderd(it) ? 'grayscale(1)' : undefined,
                      opacity: beeldVerouderd(it) ? 0.3 : 1,
                    }}
                    // `/live-screenshot` geeft 404 zodra een frame ouder is dan
                    // 12 seconden — terecht, want een oud beeld is geen live
                    // beeld. Zonder dit toonde de browser dan een gebroken
                    // plaatje, wat leest als "kapot" terwijl er gewoon niets
                    // draait op dat scherm.
                    onError={() => setKapot((v) => new Set(v).add(String(it.creator_id)))}
                  />
                  {beeldVerouderd(it) ? (
                    <div style={{
                      position: 'absolute', left: 0, right: 0, top: '46%',
                      textAlign: 'center', fontSize: 13, fontWeight: 600,
                      color: 'var(--warn)', textShadow: '0 1px 4px #000',
                      pointerEvents: 'none',
                    }}>
                      not live — last image {sinds(it.captured_at)}
                    </div>
                  ) : null}
                  <div style={{
                    position: 'absolute', top: 4, left: 6, right: 6, padding: '2px 8px',
                    fontSize: 12, borderRadius: 4, background: 'rgba(0,0,0,.6)',
                    display: 'flex', gap: 6, alignItems: 'center',
                  }}>
                    <Plat value={(it.platform || 'fc')} />
                    <b style={{ overflow: 'hidden', textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap' }}>{it.name}</b>
                    {/* Welke post wordt hier gemaakt en wanneer gaat hij live
                        (Jordy, 08-09). Zonder dit zag je dát er iets gebeurde,
                        niet wát. */}
                    {it.item && it.of_total ? (
                      <span className="mut2">· item {it.item}/{it.of_total}</span>
                    ) : null}
                    {it.volgende ? (
                      <span className="mut2">· goes live {klokje(it.volgende)}</span>
                    ) : null}
                    {/* 11-09 (Jordy): "hij staat al een tijdje uit maar live
                        browser is nog steeds bezig". De tegel zei "publishing"
                        terwijl de laatste post 43 minuten oud was. De
                        sessies blijven namelijk in de pool staan en de
                        opruiming gebruikt ze, dus de tegel blijft verversen —
                        maar de STAP eronder was allang klaar. Bij een oude
                        stand zeggen we dat, in plaats van de laatste stap te
                        herhalen alsof hij nu loopt. */}
                    {it.step && !beeldVerouderd(it) ? (
                      <span className="mut2">· {it.step}</span>
                    ) : null}
                    {(() => {
                      const pv = postVan(it)
                      const oud = pv ? standLeeftijd(pv) : null
                      if (oud === null || oud <= STAND_VERS_S) return null
                      return (
                        <span className="mut2">
                          · not posting — last step {oud < 3600
                            ? `${Math.floor(oud / 60)}m ago`
                            : `${Math.floor(oud / 3600)}h ago`}
                        </span>
                      )
                    })()}
                    {/* Het beeld blijft altijd staan — de route levert het
                        laatste frame in plaats van een 404 — maar zodra het
                        ouder is dan een paar tellen wordt de leeftijd grijs,
                        zodat stilstand herkenbaar is zonder zwart scherm. */}
                    <span
                      className="mut2"
                      style={{
                        marginLeft: 'auto',
                        color: (leeftijdS(it.captured_at) ?? 0) > VERS_S
                          ? 'var(--mut)' : undefined,
                      }}
                    >
                      {sinds(it.captured_at)}
                    </span>
                  </div>
                  {/* De afvinklijst over het beeld: waar staat deze post nu. */}
                  {(() => {
                    const pv = postVan(it)
                    if (!pv) return null
                    const oud = standLeeftijd(pv)
                    const verouderd = oud !== null && oud > STAND_VERS_S
                    return (
                      <div style={{ position: 'absolute', left: 6, right: 6, bottom: 6,
                                    background: 'rgba(0,0,0,.72)', borderRadius: 4,
                                    padding: '4px 6px',
                                    // Verouderd = grijs, zodat een oude stand
                                    // nooit meer als lopend leest.
                                    opacity: verouderd ? 0.45 : 1 }}>
                        {Stappenlijst({ post: pv, klein: true })}
                        {oud !== null ? (
                          <div className="mut2" style={{ fontSize: 10, marginTop: 2 }}>
                            {verouderd ? 'no longer live · ' : ''}
                            {oud < 60 ? `${oud}s ago`
                              : `${Math.floor(oud / 60)}m ${oud % 60}s ago`}
                          </div>
                        ) : null}
                      </div>
                    )
                  })()}
                </>
              ) : it ? (
                <div style={{ textAlign: 'center', padding: 10 }}>
                  <div style={{ fontSize: 12 }}><b>{it.name}</b></div>
                  <div className="mut2" style={{ fontSize: 12 }}>
                    {it.state === 'verifying'
                      ? 'verifying creator identity'
                      : (it.step || 'running')}
                  </div>
                  <div className="mut2" style={{ fontSize: 11, marginTop: 4 }}>
                    {/* Tijdens het openen van de sessie is er met opzet geen
                        beeld: tot de identiteit vaststaat weten we niet wie er
                        in die sessie zit, en een paneel met de naam van model A
                        boven de sessie van model B is precies de verwarring
                        waar de isolatie tegen beschermt. Vroeger stond hier een
                        zwart vlak; dat las als "kapot" terwijl dit de fase is
                        die het langst duurt. */}
                    {it.state === 'verifying'
                      ? `signing in and confirming identity${
                          typeof it.seconds === 'number' ? ` — ${it.seconds}s` : ''
                        } · no image until confirmed`
                      : 'no live image — the browser page for this model is not open'}
                  </div>
                </div>
              ) : (
                <span className="mut2" style={{ fontSize: 12 }}>
                  Screen {i + 1} — idle
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )

  /**
   * Live Steps als tabel: één rij per model, één kolom per stap.
   *
   * Waarom een tabel (Jordy, 09-09)
   * -------------------------------
   * De stappen stonden per post als een rijtje tekst. Met vijf modellen
   * tegelijk gaf dat vijf blokjes die elk hun eigen breedte namen, dus de
   * stappen stonden nooit onder elkaar en je kon twee modellen niet
   * vergelijken. Je zag wel dát iemand bij "Upload media" stond, niet wie er
   * achterliep.
   *
   * In een tabel staat elke stap in zijn eigen kolom. Eén blik langs een
   * kolom zegt wie er nog moet beginnen; één blik langs een rij zegt waar dit
   * model is. De volgorde van de kolommen is de volgorde van de code
   * (src/services/posting/stappen.py), links naar rechts.
   *
   * Alle actieve modellen krijgen een rij, ook zonder lopende post. Een model
   * dat tussen twee posts in zit verdween anders uit de tabel en dan lijkt het
   * alsof het uit staat.
   *
   * De hele tabel zit in één scrollgebied met een vaste hoogte. Daarvóór stond
   * de lopende groep búiten het scrollgebied, dus bij vijf modellen groeide het
   * paneel door zijn eigen 300px heen en liepen de stapteksten over het blok
   * VAULT DELETE eronder.
   */
  const Stappen = ({ label, platform }: { label: string; platform: string }) => {
    const posts = (stappen.data?.posts ?? [])
      .filter((pv) => (pv.platform || 'fancentro') === platform)
      .sort((a, b) => (b.started_at ?? 0) - (a.started_at ?? 0))

    /** De nieuwste post per model: dat is de rij die dit model nu toont. */
    const perModel = new Map<string, PostStappen>()
    for (const pv of posts) {
      const sleutel = String(pv.model_persona_id ?? pv.model)
      if (!perModel.has(sleutel)) perModel.set(sleutel, pv)
    }

    /** De rijen: alle actieve modellen alfabetisch, en daarachter modellen die
     *  niet (meer) actief zijn maar vandaag wel gepost hebben. Die laatste
     *  weghalen zou het spoor van vannacht wissen. */
    const actief = platform === 'fancentro'
      ? [...(modellen.data?.models ?? [])].filter((m) => m.active)
          .sort((a, b) => a.name.localeCompare(b.name, 'nl'))
      : []
    const rijen: { sleutel: string; naam: string; post?: PostStappen }[] = []
    const gehad = new Set<string>()
    for (const m of actief) {
      const sleutel = String(m.model_persona_id)
      gehad.add(sleutel)
      rijen.push({ sleutel, naam: m.name, post: perModel.get(sleutel) })
    }
    for (const [sleutel, pv] of perModel) {
      if (gehad.has(sleutel)) continue
      rijen.push({ sleutel, naam: pv.model, post: pv })
    }

    /** De kolommen komen uit de eerste post die we hebben; staat er nog geen
     *  post, dan de vaste FC-volgorde. Zo klopt de kop ook voor F2F, dat vijf
     *  stappen heeft en een andere vierde. */
    const kolommen: { key: string; label: string }[] =
      (posts[0]?.steps ?? []).map((st) => ({ key: st.key, label: st.label }))
      || []
    const kop = kolommen.length ? kolommen : (platform === 'f2f'
      ? [{ key: 'session', label: 'Impersonate creator' },
         { key: 'identity', label: 'Verify creator identity' },
         { key: 'media', label: 'Upload media' },
         { key: 'caption', label: 'Fill caption' },
         { key: 'publish', label: 'Submit and verify' }]
      : [{ key: 'session', label: 'Open creator session' },
         { key: 'identity', label: 'Verify creator identity' },
         { key: 'media', label: 'Upload media to vault' },
         { key: 'publish', label: 'Create post' },
         { key: 'verify', label: 'Verify post appeared' },
         { key: 'lifecycle', label: 'Expiry and vault delete' }])

    const cel = (st: Stap | undefined) => {
      if (!st) return <span className="mut2">·</span>
      // 16-09 (POSTING-002): 'waiting' is geen fout. Een post die wacht op
      // FanCentro's daglimiet of op een van hun korte tempo-vensters blijft
      // gewoon staan; die hoort niet rood te zijn.
      const kleur = st.state === 'done' ? 'var(--ok)'
        : st.state === 'running' ? 'var(--warn)'
        : st.state === 'failed' ? 'var(--bad)'
        : st.state === 'waiting' ? 'var(--warn)'
        : st.state === 'moved' ? 'var(--acc)'
        : st.state === 'skipped' ? 'var(--acc)'
        : 'var(--mut)'
      const teken = st.state === 'done' ? '✓' : st.state === 'failed' ? '✕'
        : st.state === 'waiting' ? '⏳'
        : st.state === 'moved' ? '↦'
        : st.state === 'skipped' ? '⊘' : st.state === 'running' ? '●' : '○'
      return (
        <span title={st.reason ? `${st.label} — ${st.reason}` : st.label}
              style={{ whiteSpace: 'nowrap' }}>
          <span style={{ color: kleur }}>{teken}</span>
          {st.seconds != null ? (
            <span className="mut2" style={{ marginLeft: 4 }}>
              {st.seconds.toFixed(1)}s
            </span>
          ) : null}
        </span>
      )
    }

    const loopt = rijen.some((r) => r.post && !r.post.finished_at)

    return (
      <div className="card monitorpane">
        <div className="between" style={{ marginBottom: 10 }}>
          <b style={{ fontSize: 12 }}>{label}</b>
          <Tag kind={loopt ? 'ok' : 'mut'}>
            {loopt ? <><span className="dotst on-live" />Live</> : 'Idle'}
          </Tag>
        </div>
        <div className="panescroll stappentabel">
          {rijen.length === 0 ? (
            <div className="empty" style={{ padding: 18 }}>
              {liveLoading ? 'Loading…'
                : 'Nothing posting right now. When a post runs, every step '
                  + 'appears here per model.'}
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Model</th>
                  {kop.map((k) => (
                    <th key={k.key} title={k.label}>{KOP[k.key] ?? k.label}</th>
                  ))}
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rijen.map((r) => {
                  const pv = r.post
                  const mislukt = pv?.steps.find((st) => st.state === 'failed')
                  // Wachten op het platform staat apart: de post is niet
                  // mislukt, hij komt in een volgende ronde gewoon terug.
                  const wacht = pv?.steps.find((st) => st.state === 'waiting')
                  // Doorgeschoven is geen mislukking: de post komt terug op een
                  // later moment, met dat tijdstip in de reden.
                  const verplaatst = pv?.steps.find((st) => st.state === 'moved')
                  return (
                    <tr key={r.sleutel}>
                      <td>
                        <b>{r.naam}</b>
                        {pv && pv.item ? (
                          <span className="mut2">
                            {` · ${pv.item}/${pv.of_total ?? '?'}`}
                            {pv.cycle ? ` · cycle ${pv.cycle}` : ''}
                          </span>
                        ) : null}
                      </td>
                      {kop.map((k) => (
                        <td key={k.key} className="num">
                          {cel(pv?.steps.find((st) => st.key === k.key))}
                        </td>
                      ))}
                      <td>
                        {!pv ? (
                          <span className="mut2">waiting</span>
                        ) : verplaatst ? (
                          <span style={{ color: 'var(--acc)' }}
                                title={verplaatst.reason || undefined}>
                            {verplaatst.reason || 'moved to a later slot'}
                          </span>
                        ) : wacht ? (
                          <span style={{ color: 'var(--warn)' }}
                                title={wacht.reason || undefined}>
                            {wacht.reason ? `waiting: ${wacht.reason}` : 'waiting'}
                          </span>
                        ) : mislukt ? (
                          <span style={{ color: 'var(--bad)' }}
                                title={mislukt.reason || undefined}>
                            {mislukt.reason
                              ? `failed: ${mislukt.reason}` : 'failed'}
                          </span>
                        ) : pv.finished_at ? (
                          <span className="mut2">
                            {`done ${new Date(pv.finished_at * 1000)
                              .toLocaleTimeString([], { hour: '2-digit',
                                                        minute: '2-digit' })}`}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--ok)' }}>running</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    )
  }

  return (
    <Panel
      title="Live Bot Monitor"
      desc="Live · updates as posts run"
      right={<Tag kind="ok"><span className="dotst on-live" />Live browser</Tag>}
    >
      {note ? (
        <div className="mut2" style={{ fontSize: 12, marginBottom: 10 }}>{note}</div>
      ) : null}

      {/* Het aantal schermen is de capaciteit, maar nooit minder dan wat er
          werkelijk draait: liever een scherm extra dan een post die nergens te
          zien is. */}
      {/* 15-09 (Jordy: "alle tegels knipperen"). `Blok`, `Stappen` en
          `Stappenlijst` worden binnen deze functie gemaakt. Als JSX-element
          (`<Blok/>`) is dat bij elke render een ánder componenttype, dus
          gooide React elke twee seconden alle tegels weg en bouwde ze opnieuw:
          een leeg zwart vlak tot het beeld er weer was. Als gewone aanroep
          blijven dezelfde elementen staan en verandert alleen wat echt
          verandert. */}
      {Blok({ label: 'FanCentro', rijen: fcTegels,
              aantal: Math.max(slots.fc, fcTegels.length) })}
      {Blok({
        label: 'F2F',
        rijen: f2fItems,
        aantal: Math.max(slots.f2f, f2fItems.length),
        uitleg: slots.f2f < 2
          ? 'F2F publishes through one shared logged-in browser page, so it runs '
            + 'one post at a time. This block grows once F2F gets a per-creator '
            + 'session pool of its own.'
          : undefined,
      })}

      {/* Live steps per platform naast elkaar; onder 1400px onder elkaar,
          zodat een stap nooit half wegvalt. */}
      <div className="grid livesteps" style={{ marginBottom: 16 }}>
        {Stappen({ label: 'FC · LIVE STEPS', platform: 'fancentro' })}
        {Stappen({ label: 'F2F · LIVE STEPS', platform: 'f2f' })}
      </div>

      {/* 16-09 (Jordy): direct onder de live steps één rij per model met
          rondes, wachtrij, live, vault en mislukt. Zie ModelOverzicht.tsx. */}
      <ModelOverzicht />

      {/* De vier monitorpanelen staan in oud ONDER elkaar op volle breedte
          (1566x300 elk), niet naast elkaar. "TOTALEN" bestaat daar niet;
          POSTS IN PROGRESS en RECENTLY PUBLISHED wel. */}
      <div className="grid" style={{ gridTemplateColumns: 'minmax(0,1fr)', marginTop: 16 }}>
        <div className="card monitorpane">
          <div className="between" style={{ marginBottom: 10 }}>
            <b style={{ fontSize: 12 }}>VAULT DELETE · LIVE</b>
            <Tag kind="acc">
              {num(vault?.tracked_bot_vault_rows)} tracked · {num(vault?.remaining_due)} due
            </Tag>
          </div>
          <div className="row" style={{ gap: 14, marginBottom: 8, fontSize: 12 }}>
            <span><b>{num(vault?.by_status?.published)}</b> ok (recent)</span>
            <span><b>{num(vault?.remaining_due)}</b> due</span>
            <span><b>{num(vault?.deleted_success_since_restart)}</b> deleted (this run)</span>
          </div>
          <div className="note" style={{ fontSize: 12 }}>
            Tracked count is recent bot DB rows. The full FanCentro vault is also
            swept via post-history proof — <b>manual uploads are never deleted.</b>
          </div>
        </div>

        <div className="card monitorpane">
          <div className="between" style={{ marginBottom: 10 }}>
            <b style={{ fontSize: 12 }}>POSTS IN PROGRESS</b>
            <span className="mut2" style={{ fontSize: 12 }}>(scroll · active first)</span>
          </div>
          <div className="panescroll">
            {inProgress.length === 0 ? (
              <div className="empty" style={{ padding: 18 }}>No posts in progress.</div>
            ) : inProgress.map((r) => (
              <div className="feedrow" key={r.id}>
                <Plat value={r.platform} />
                <span className="fmodel">{r.model ?? '—'}</span>
                <span className="ftext">{r.caption ?? ''}</span>
                <Tag kind={r.status === 'failed' ? 'bad' : 'warn'}>{r.status}</Tag>
                <span className="mut2 ftime">{r.at ?? ''}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card monitorpane">
          <div className="between" style={{ marginBottom: 10 }}>
            <b style={{ fontSize: 12 }}>FAILED POSTS · 24 HOURS</b>
            <Tag kind={(fouten.data?.count ?? 0) ? 'bad' : 'ok'}>
              {fouten.data?.count ?? 0}
            </Tag>
          </div>
          <div className="panescroll">
            {!(fouten.data?.items ?? []).length ? (
              <div className="empty" style={{ padding: 18 }}>
                No failed posts in the last 24 hours.
              </div>
            ) : (fouten.data?.items ?? []).map((f) => (
              <div className="feedrow" key={f.id} style={{ flexWrap: 'wrap' }}>
                <Plat value={f.platform} />
                <span className="fmodel">
                  {modelNaam(f.model_persona_id, modellen.data?.models)}
                </span>
                <span className="ftext">
                  {f.caption ?? ''}
                  {/* De reden voluit, niet afgekapt: "FC tags invalid" zonder
                      de rest zegt niets over wat je moet repareren. */}
                  <span className="mut2" style={{ display: 'block', fontSize: 11 }}>
                    {f.error_message || 'no reason recorded'}
                    {f.retry_count ? ` · attempt ${f.retry_count}` : ''}
                    {(f.upload_pogingen ?? 0) > 1
                      ? ` · photo uploaded ${f.upload_pogingen}×` : ''}
                  </span>
                </span>
                <Tag kind={f.ui_status === 'skipped' ? 'mut' : 'bad'}>
                  {f.ui_status || f.status}
                </Tag>
                <span className="mut2 ftime">{klokje(f.updated_at)}</span>
                {bewerkKnop({ id: f.id, status: f.status, created_by: f.created_by,
                              caption: f.caption, description: f.description })}
                {bewerkVak(f.id)}
              </div>
            ))}
          </div>
        </div>

        <div className="card monitorpane">
          <div className="between" style={{ marginBottom: 10 }}>
            <b style={{ fontSize: 12 }}>POSTS THAT KEEP FAILING</b>
            <Tag kind={(steeds.data?.needs_attention ?? 0) ? 'bad'
                       : (steeds.data?.count ?? 0) ? 'warn' : 'ok'}>
              {steeds.data?.count ?? 0}
            </Tag>
          </div>
          <div className="panescroll">
            {!(steeds.data?.items ?? []).length ? (
              <div className="empty" style={{ padding: 18 }}>
                No post is being refused repeatedly.
              </div>
            ) : (steeds.data?.items ?? []).map((s) => (
              <div className="feedrow" key={s.post_id} style={{ flexWrap: 'wrap' }}>
                <Plat value={s.platform ?? 'fancentro'} />
                <span className="fmodel">
                  {s.model || modelNaam(s.model_persona_id, modellen.data?.models)}
                </span>
                <span className="ftext">
                  {`#${s.post_id} · ${s.caption ?? ''}`}
                  <span className="mut2" style={{ display: 'block', fontSize: 11 }}>
                    {`last reason: ${s.last_reason || 'unknown'} · ${s.attempts} failed attempts`}
                    {s.in_a_row ? ` · ${s.in_a_row} in a row` : ''}
                    {s.times_parked ? ` · parked ${s.times_parked}×` : ''}
                  </span>
                </span>
                {s.needs_attention ? (
                  <Tag kind="bad" title="Parked again and again: FanCentro keeps refusing this post. Check the text or the photo.">
                    needs attention
                  </Tag>
                ) : null}
                <span className="mut2 ftime">
                  {s.parked_until ? `parked until ${klokje(s.parked_until)}` : s.status}
                </span>
                {bewerkKnop({ id: s.post_id, status: s.status, created_by: s.created_by,
                              caption: s.caption, description: s.description })}
                {bewerkVak(s.post_id)}
              </div>
            ))}
          </div>
        </div>

        <div className="card monitorpane">
          <div className="between" style={{ marginBottom: 10 }}>
            <b style={{ fontSize: 12 }}>PUBLISHED · FULL HISTORY</b>
            <Tag kind="ok">
              <span className="dotst on-live" />
              updated {recentGeladen ? recentGeladen.toLocaleTimeString('en-GB') : '—'}
            </Tag>
          </div>
          <div className="row" style={{ flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            <select className="select" value={recentPlatform}
                    onChange={(e) => { setRecentPlatform(e.target.value); setRecentPagina(0) }}>
              <option value="all">All platforms</option>
              <option value="fancentro">FanCentro</option>
              <option value="f2f">F2F</option>
            </select>
            <select className="select" value={recentModel}
                    onChange={(e) => { setRecentModel(e.target.value); setRecentPagina(0) }}>
              <option value="all">All models</option>
              {(recent.data?.models ?? []).map((m) => (
                <option key={m.model_persona_id} value={String(m.model_persona_id)}>
                  {`${m.model ?? m.model_persona_id} (${num(m.count)})`}
                </option>
              ))}
            </select>
            <span className="mut2" style={{ fontSize: 12 }}>
              {recentTotaal
                ? `${num(recentPagina * RECENT_PER_PAGINA + 1)}–${num(Math.min(recentTotaal, (recentPagina + 1) * RECENT_PER_PAGINA))} of ${num(recentTotaal)}`
                : ''}
            </span>
            <button className="btn ghost sm" type="button" disabled={recentPagina === 0}
                    onClick={() => setRecentPagina((p) => Math.max(0, p - 1))}>Newer</button>
            <button className="btn ghost sm" type="button"
                    disabled={(recentPagina + 1) * RECENT_PER_PAGINA >= recentTotaal}
                    onClick={() => setRecentPagina((p) => p + 1)}>Older</button>
          </div>
          <div className="panescroll">
            {published.length === 0 ? (
              <div className="empty" style={{ padding: 18 }}>Nothing published yet.</div>
            ) : published.map((r) => (
              <div className="feedrow" key={r.id}>
                <Plat value={r.platform} />
                <span className="fmodel">{r.model ?? '—'}</span>
                <span className="ftext">{r.caption ?? ''}</span>
                {r.status === 'removed'
                  ? <Tag kind="mut">removed</Tag>
                  : r.status !== 'live' && r.status !== 'on schedule'
                    ? <Tag kind="warn">{r.status}</Tag> : null}
                <span className="mut2 ftime" style={{ whiteSpace: 'normal', textAlign: 'right' }}>
                  <span title="Exact moment the post was created on the platform">pushed {r.pushed}</span>
                  {' · '}
                  <span style={r.isLive && r.status === 'live' ? { color: 'var(--ok)' } : undefined}>
                    {r.isLive ? 'went live' : 'goes live'} {r.live}
                  </span>
                  {' · '}expires {r.expires}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Panel>
  )
}
